<template>
    <div class="form-client-details">
        <ElForm class="form-client-details__form form" 
            ref="clientFormRef" 
            :model="clientForm"
            :rules="rules"
            >
            <ElFormItem class="form__item"
                prop="inn"
                :error="innErrorText"
                >
                <ElInput v-model="clientForm.inn" 
                    placeholder="Введите ИНН" 
                    v-mask="{regex: '^[0-9]{10}[0-9]{0,2}'}"
                    :disabled="innIsDefined"
                    @input="innErrorText = ''"
                    >
                    <template #prefix>
                        ИНН
                    </template>
                    <i class="form__item-icon-lock" slot="suffix"></i>
                </ElInput>
            </ElFormItem>

            <ElFormItem v-if="innIsDefined" 
                class="form__item">
                <ElInput v-model="clientForm.name"
                    placeholder="Наименование"
                    :disabled="innIsDefined">
                    <template #prefix>
                        Наименование
                    </template>
                    <i class="form__item-icon-lock" slot="suffix"></i>
                </ElInput>
            </ElFormItem>

            <ElFormItem v-show="innIsDefined" 
                class="form__item"
                prop="address"
                >
                <ElAutocomplete class="form__item" 
                    v-model="clientForm.address" 
                    :fetch-suggestions="fetchAddressSuggestions"
                    placeholder="Выберите адрес" 
                    :popper-append-to-body="false"
                    value-key="value"
                    v-on:input="clearParams"
                    @select="handleSelect"
                    >
                    <template #prefix v-if="clientForm.type_of_client === organizationTypesMap.individual">
                        Адрес регистрации
                    </template>
                    <template #prefix v-else>
                        Юридический адрес
                    </template>
                </ElAutocomplete>
            </ElFormItem>

            <div v-if="clientIsOrganization" 
                class="form__item form__item--col">
                <ElFormItem class="form__col form__col--half"
                    prop="management_name">
                        <ElInput v-model="clientForm.management_name" 
                            placeholder="ФИО руководителя">
                            <template #prefix>
                                ФИО руководителя
                            </template>
                        </ElInput>
                </ElFormItem>
                <ElFormItem class="form__col form__col--half"
                    prop="management_post"
                    >
                    <ElInput 
                        v-model="clientForm.management_post" 
                        placeholder="Должность руководителя">
                        <template #prefix>
                            Должность руководителя
                        </template>
                    </ElInput>
                </ElFormItem>
            </div>

            <ElFormItem v-if="clientIsPerson"
                class="form__item"
                prop="passport_number">
                    <ElInput v-model="clientForm.passport_number" 
                        placeholder="Серия и номер паспорта"
                        v-mask="{regex: '^[0-9]${2} [0-9]${2} [0-9]${6}'}"
                        >
                        <template #prefix>
                            Серия и номер паспорта
                        </template>
                    </ElInput>
            </ElFormItem>

            <div v-if="clientIsPerson" 
                class="form__item form__item--col">
                <ElFormItem class="form__col form__col--half"
                    prop="birth_date">
                    <ElDatePicker v-model="clientForm.birth_date"
                        v-mask="{mask: '9{2}.9{2}.9{4}'}"
                        ref="birthPicker"
                        type="date"
                        placeholder="дд.мм.гггг"
                        :default-value="subYears(new Date(), 18)"
                        format="dd.MM.yyyy"
                        value-format="dd.MM.yyyy"
                        :picker-options="{ disabledDate: (time) => disableInvalidBirthDates(time) }"
                        >
                    </ElDatePicker>
                    <span class="form__label">Дата рождения</span>
                </ElFormItem>

                <ElFormItem class="form__col form__col--half"
                    prop="passport_issue_date"
                    >
                    <ElDatePicker v-model="clientForm.passport_issue_date"
                        v-mask="{mask: '9{2}.9{2}.9{4}'}"
                        type="date"
                        placeholder="дд.мм.гггг"
                        format="dd.MM.yyyy"
                        value-format="dd.MM.yyyy"
                        :default-value="passportIssueMinDate"
                        :disabled="!birthDateIsFilled"
                        :picker-options="{ disabledDate: (time) => disableInvalidPassportIssueDates(time) }"
                        @click.native="closeBirthPicker"
                        >
                    </ElDatePicker>
                    <span class="form__label">Дата выдачи паспорта</span>
                </ElFormItem>
            </div>

            <div v-if="innIsDefined" 
                class="form__item form__item--col">
                <ElTooltip placement="bottom-start"
                    :content="'Будьте внимательны, указанный номер будет использоваться для управления подпиской'">
                    <ElFormItem class="form__col form__col--half"
                    prop="phone_number">
                        <ElInput v-model="clientForm.phone_number" 
                            placeholder="Телефон"
                            v-mask="{regex: '^\\+7 \\([0-9]${3}\\) [0-9]${3} [0-9]${2}-[0-9]${2}'}"
                            >
                            <template #prefix>
                                Телефон
                            </template>
                        </ElInput>
                    </ElFormItem>
                </ElTooltip>
                <ElFormItem class="form__col form__col--half"
                    prop="email"
                    >
                    <ElInput 
                        v-model="clientForm.email" 
                        type="email"
                        placeholder="E-mail">
                        <template #prefix>
                            E-mail
                        </template>
                    </ElInput>
                </ElFormItem>
            </div>

            <div class="form__buttons">
                <BaseButton text="Назад" 
                    theme="muted" 
                    is-text 
                    @click.native="onClose" />
                <BaseButton text="Продолжить" 
                    size="large" 
                    :is-disabled="submitIsDisabled"
                    @click.native="onSubmit($refs.clientFormRef)" />
            </div>
        </ElForm>
        <ModalPolicyIssuanceError title="Невозможно оформить полис"
            text="Для указанного вами юридического лица оформление полиса онлайн недоступно."
            :visible="showErrorModal"
            modal-width="719px"
            @close="showErrorModal = false" />
    </div>
</template>

<script>
import { subYears, addYears, subDays, parse } from 'date-fns';
import BaseButton from '@/components/ui/BaseButton';
import ModalPolicyIssuanceError from '@/components/modals/ModalPolicyIssuanceError';
import { numbersLengthFromString, matchesEmailRegex } from '@/utils/string-helpers';
import { validationHandler, getValidDate, clientPassportIssueIsInvalid, isValidDate } from '@/utils/validation';
import { PERSON_INN_LENGTH, PHONE_LENGTH, PERSON_PASSPORT_LENGTH, PASSPORT_ISSUE_DATE_LENGTH, INN_VALID_MAP, INN_ERRORS_MAP, ORGANIZATION_TYPES_MAP } from '@/consts';
import { getOrganizationInfo } from '@/api/routes/inn';
import { fetchAddressSuggestions } from '@/utils/api-helpers';

export default {
    name: 'FormClientDetails',
    components: {
        BaseButton,
        ModalPolicyIssuanceError
    },
    data() {
        return {
            clientForm: {
                inn: '',
                name: '',
                address: '',
                phone_number: '',
                email: '',
                type_of_client: '',
                management_name: '',
                management_post: '',
                passport_number: '',
                birth_date: '',
                passport_issue_date: '',
                fias_id: '',
                house: '',
                houseGuid: '',
                isSetSuggest: false,
                isSetSuggestManual: false,
            },
            rules: {
                inn: [
                    { required: true, message: 'Поле ИНН обязательно для заполнения', trigger: 'blur' },
                    { 
                        validator: (rule, val, resolve) => validationHandler(this.innLengthIsValid, `Поле ИНН должно содержать минимум ${PERSON_INN_LENGTH} символов`, resolve),
                        trigger: 'blur'
                    }
                ],
                address: [

                    { required: true, message: 'Поле адреса обязательно для заполнения', trigger: 'blur' },
                    {
                        validator: (rule, val, resolve) => validationHandler(this.addressIsValid, this.addressError, resolve),
                        trigger: 'blur'
                    }
                ],
                email: [
                    { required: true, message: 'Введите e-mail', trigger: 'blur' },
                    { 
                        validator: (rule, val, resolve) => validationHandler(this.emailFormatIsValid, 'Неверный формат электронной почты!', resolve),
                        trigger: 'blur' 
                    },
                ],
                phone_number: [
                    { required: true, message: 'Введите номер телефона', trigger: 'blur' },
                    {
                        validator: (rule, val, resolve) => validationHandler(this.phoneLengthIsValid, `Номер телефона должен содержать ${PHONE_LENGTH} символов`, resolve), 
                        trigger: 'blur' 
                    },
                ],
                passport_number: [
                    { required: true, message: 'Введите серию и номер паспорта', trigger: 'blur' },
                    {
                        validator: (rule, val, resolve) => validationHandler(this.passportLengthIsValid, `Данные должны содержать ${PERSON_PASSPORT_LENGTH} символов`, resolve), 
                        trigger: 'blur' 
                    },
                ],
                management_name: [
                    { required: true, message: 'Заполните ФИО руководителя', trigger: 'blur' }
                ],
                management_post: [
                    { required: true, message: 'Заполните должность руководителя', trigger: 'blur' }
                ],
                passport_issue_date: [
                    {
                        validator: (rule, val, resolve) => {
                            validationHandler(this.validatePassportIssueDate, `Дата выдачи паспорта указана неверно`, resolve)
                        }, 
                        trigger: 'blur' 
                    },
                    { required: true, message: 'Введите дату выдачи паспорта', trigger: 'change' },
                ],
            },
            innErrorText: '',
            innIsDefined: false,
            showErrorModal: false,
        }
    },
    methods: {
        async onSubmit(formEl) {
            if (!this.innIsDefined) {
                this.validateOrganizationByInn();
            } else {
                formEl.validate(valid => {
                    if (valid) {
                        this.$emit('client-details-submit', this.clientForm);
                    }
                })
            }
        },
        async validateOrganizationByInn() {
            this.innErrorText = '';

            try {
                const organizationData = await getOrganizationInfo(this.clientForm.inn);
    
                const innValidatonStatus = typeof organizationData === 'string' 
                    ? INN_VALID_MAP[organizationData]
                    : INN_VALID_MAP[organizationData?.valid_value]
    
                if (innValidatonStatus === 'success') {
                    // ИНН найден и разрешен
                    Object.assign(this.clientForm, organizationData);
                    
                    this.innIsDefined = true;
                    this.$emit('inn-is-defined');
                    this.$refs.clientFormRef.validateField('address');
                } else if (innValidatonStatus === 'forbidden') {
                    // ИНН найден, но ОКВЭД запрещен
                    this.showErrorModal = true;
                } else if (innValidatonStatus === 'not_found') {
                    // ИНН не найден, Ошибка поиска юридического лица по указанному ИНН
                    this.innErrorText = INN_ERRORS_MAP[innValidatonStatus];
                } else {
                    // Произошла непредвиденная ошибка
                    this.innErrorText = INN_ERRORS_MAP.uncaught_error;
                }
            } catch(err) {
                this.innErrorText = INN_ERRORS_MAP.uncaught_error;
            }
        },
        closeBirthPicker(e) {
            //Закрытие пикера с датой рождения, дополниельный обработчик потому по умолчанию
            // пикер с датой выдачи паспорта в disable и событие по умолчанию не отрабатывает
            this.$refs.birthPicker.handleClose();
        },
        validatePassportIssueDate() {
            if (!this.clientForm.passport_issue_date) {
                return false
            }
            return this.clientPassportIssueIsInvalid(this.clientForm.birth_date, this.clientForm.passport_issue_date);
        },
        disableInvalidBirthDates(birthDate) {
            const currentDate = new Date();       
            const eighteenYearsAgo = subYears(currentDate, 18);

            return birthDate.getTime() > eighteenYearsAgo.getTime();
        },
        disableInvalidPassportIssueDates(issueDate) {
            if (!this.birthDateIsFilled) {
                return true
            }

            const birthDate = parse(this.clientForm.birth_date, 'dd.MM.yyyy', new Date());
            const today = new Date();

            let birthPlus14Years = addYears(birthDate, 14);
            
            if (issueDate < birthPlus14Years) {
                // дата выдачи паспорта должна быть больше чем дата рождения + 14 лет
                return true
            } else if (issueDate > subDays(today, 1)) {
                // дата выдачи паспорта должна быть меньше чем сегодня - один день
                return true
            }

            return false
        },
        onClose() {
            if (this.innIsDefined) {
                this.$emit('prev-step');
            } else {
                this.$emit('close');
            }

            this.innIsDefined = false;
        },
        clearParams(val) {
            this.clientForm.isSetSuggest = false;
            this.clientForm.isSetSuggestManual = false;
            this.clientForm.house = '';
            this.clientForm.houseGuid = '';
            this.clientForm.fias_id = '';
        },

        handleSelect(item) {
            this.clientForm.isSetSuggest = true;

            if (item?.code && item.code === 'user_manual') {
                this.clientForm.isSetSuggestManual = true;
            }

            this.clientForm.house = item.house;
            this.clientForm.houseGuid = item.houseGuid;
            this.clientForm.fias_id = item.fiasId;

            this.$refs.clientFormRef.validateField('address');
        },

        clientPassportIssueIsInvalid,
        getValidDate,
        subYears
    },
    computed: {
        addressError() {
            if (!this.clientForm.isSetSuggest) {
                return 'Не выбран адрес из списка';
            }

            if (this.clientForm.isSetSuggest && !this.clientForm.isSetSuggestManual && !!!this.clientForm.houseGuid) {
                return 'Не указан номер дома';
            }

            return 'Поле обязательно для заполнения'
        },
        addressIsValid() {
            if (!this.clientForm.isSetSuggest) {
                return false;
            }

            if (this.clientForm.isSetSuggest && !this.clientForm.isSetSuggestManual && !!!this.clientForm.houseGuid) {
                return false;
            }

            return true;
        },
        innLengthIsValid() {
            return numbersLengthFromString(this.clientForm.inn) >= PERSON_INN_LENGTH;
        },
        phoneLengthIsValid() {
            return numbersLengthFromString(this.clientForm.phone_number) >= PHONE_LENGTH
        },
        emailIsFilled() {
            return this.clientForm.email.length !== 0
        },
        birthDateIsFilled() {
            return !!this.clientForm.birth_date?.length;
        },
        emailFormatIsValid() {
            return matchesEmailRegex(this.clientForm.email);
        },
        passportLengthIsValid() {
            return numbersLengthFromString(this.clientForm.passport_number) >= PERSON_PASSPORT_LENGTH;
        },
        passportIssueLengthIsValid() {
            return numbersLengthFromString(this.clientForm.passport_issue_date) >= PASSPORT_ISSUE_DATE_LENGTH;
        },
        passportIssueMinDate() {
            if (!this.clientForm.birth_date) {
                return '';
            }
            const birthDate = parse(this.clientForm.birth_date, 'dd.MM.yyyy', new Date());
            return addYears(birthDate, 14)
        },
        organizationTypesMap() {
            return ORGANIZATION_TYPES_MAP;
        },
        submitIsDisabled() {
            switch(this.innIsDefined) {
                case false:
                    return !this.innLengthIsValid;
                case true:
                    const clientType = this.clientForm.type_of_client;

                    if (clientType === ORGANIZATION_TYPES_MAP.individual) {
                        return !this.phoneLengthIsValid 
                            || !this.passportLengthIsValid 
                            || !this.passportIssueLengthIsValid 
                            || !this.emailIsFilled;
                        
                    } else if (clientType === ORGANIZATION_TYPES_MAP.company) {
                        return !this.phoneLengthIsValid || !this.emailIsFilled;;
                    }
            }
        },
        clientIsOrganization() {
            return this.clientForm.type_of_client === this.organizationTypesMap.company && this.innIsDefined
        },
        clientIsPerson() {
            return this.clientForm.type_of_client === this.organizationTypesMap.individual && this.innIsDefined
        },

        fetchAddressSuggestions
    },
}
</script>

<style scoped lang=scss>
.form-client-details {}
</style>